import React from 'react';
import {
  CCAConfigQuestionEnriched,
  CCAQuestion,
} from '../../../../../../../utils/types/cca_types';
import { getSubdomainColor, toTitleCase } from '../utils/ccaHelpers';

interface Props {
  assignedQuestions: CCAConfigQuestionEnriched[];
  questionMap: Record<string, CCAQuestion>;
  isLoading: boolean;
  isGenerating?: boolean;
  onRemove?: (questionId: string) => void;
  onClearAll?: () => void;
}

export const AssignedQuestionsList: React.FC<Props> = ({
  assignedQuestions,
  questionMap,
  isLoading,
  isGenerating,
  onRemove,
  onClearAll,
}) => {
  if (isGenerating) {
    return (
      <div className='flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 py-20 text-slate-400'>
        <div className='mb-3 h-5 w-5 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent' />
        <p className='text-sm font-medium text-slate-500'>Generating questions…</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className='space-y-2'>
        { Array.from({ length: 4 }).map((_, i) => (
          <div
              key={i}
              className='h-14 w-full animate-pulse rounded-lg bg-slate-100'
          />
        )) }
      </div>
    );
  }

  if (assignedQuestions.length === 0) {
    return (
      <div className='flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 py-20 text-slate-400'>
        <svg
            className='mb-3 h-9 w-9'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
            strokeWidth={1.2}
        >
          <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M21 21l-4.35-4.35m0 0A7.5 7.5 0 1 0 4.5 4.5a7.5 7.5 0 0 0 12.15 12.15z'
          />
        </svg>
        <p className='text-sm font-medium text-slate-500'>No questions assigned yet</p>
        <p className='mt-1 text-xs text-slate-400'>
          Use Quick Build or Manual Selection to add questions
        </p>
      </div>
    );
  }

  const saCount = assignedQuestions.filter(
    (q) => q.questionType === 'short_answer',
  ).length;
  const mcCount = assignedQuestions.filter(
    (q) => q.questionType === 'multiple_choice',
  ).length;

  return (
    <div>
      { /* Header */ }
      <div className='mb-3 flex items-center justify-between'>
        <p className='text-sm font-semibold text-slate-700'>
          { assignedQuestions.length } Question
          { assignedQuestions.length !== 1 ? 's' : '' } Assigned
        </p>
        <div className='flex items-center gap-2'>
          <span className='rounded-full bg-violet-100 px-2.5 py-0.5 text-xs font-medium text-violet-700'>
            { saCount } SA
          </span>
          <span className='rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-700'>
            { mcCount } MC
          </span>
          { onClearAll && (
            <button
                type='button'
                onClick={onClearAll}
                className='ml-1 text-xs text-rose-500 transition-colors hover:text-rose-600'
            >
              Clear all
            </button>
          ) }
        </div>
      </div>

      { /* List */ }
      <div className='space-y-2'>
        { assignedQuestions.map((cq, i) => {
          const fullQ = questionMap[cq.questionId];
          return (
            <div
                key={cq.id || `${cq.questionId}-${i}`}
                className='flex items-start gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5 shadow-sm transition-shadow hover:shadow'
            >
              <span className='mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-[11px] font-bold text-emerald-700'>
                { i + 1 }
              </span>
              <div className='min-w-0 flex-1'>
                { fullQ ? (
                  <p className='text-sm text-slate-800'>{ fullQ.question.text }</p>
                ) : (
                  <p className='text-sm italic text-slate-400'>Loading…</p>
                ) }
                <div className='mt-1.5 flex flex-wrap gap-1.5'>
                  <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${getSubdomainColor(cq.subdomain)}`}
                  >
                    { toTitleCase(cq.subdomain) }
                  </span>
                  <span className='rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-600'>
                    { cq.questionType === 'multiple_choice' ? 'MC' : 'SA' }
                  </span>
                  { fullQ && (
                    <span className='rounded-full bg-slate-100 px-2 py-0.5 text-[10px] capitalize text-slate-500'>
                      { fullQ.question.imageKey ? 'image' : 'wordy' }
                    </span>
                  ) }
                  <span className='rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500'>
                    Lv { cq.level }
                  </span>
                </div>
                { fullQ?.idealAnswer && (
                  <p className='mt-1.5 text-xs text-slate-500'>
                    <span className='font-semibold'>Ideal answer:</span>{ ' ' }
                    { fullQ.idealAnswer }
                  </p>
                ) }
              </div>
              { onRemove && (
                <button
                    type='button'
                    onClick={() => onRemove(cq.questionId)}
                    className='shrink-0 rounded p-1 text-rose-400 transition-colors hover:bg-rose-50 hover:text-rose-600'
                    aria-label='Remove question'
                >
                  <svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
                    <path strokeLinecap='round' strokeLinejoin='round' d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' />
                  </svg>
                </button>
              ) }
            </div>
          );
        }) }
      </div>
    </div>
  );
};
